import React, { useMemo } from "react"
import { DataTable, DataTableColumn } from "@/components/ui/DataTable"
import { OvertimeRequest } from "@/types"
import { OvertimeStatus, Theme } from "@/types/enums"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Calendar, Clock, Building2 } from "lucide-react"
import { useEmployees } from '@/contexts/employees/EmployeesContext'
import { getOvertimeColorScheme, getBadgeClasses, getRowColor } from '@/utils/overtimeColorCoding'
import { OvertimeColorLegend } from '@/components/ui/OvertimeColorLegend'
import { useTheme } from "next-themes"
import { isResident, getResidencyDisplayValue } from '@/utils/residencyUtils'
import { getStatusColor } from "@/utils/statusUtils"
import { getMonthAbbreviation } from "@/constants/months"

export interface EmployeeOvertimeRow extends Record<string, unknown> {
  employeeName: string
  employeeId: string
  month: string
  hoursWorked: number
  department: string
  residency: string
}

interface EmployeeOvertimeTableProps {
  data: OvertimeRequest[]
  isLoading?: boolean
  className?: string
}

export function EmployeeOvertimeTable({ 
  data, 
  isLoading = false, 
  className 
}: EmployeeOvertimeTableProps) {
  const { employees } = useEmployees()
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === Theme.DARK

  // Group by employee and month
  const employeeTableData = useMemo<EmployeeOvertimeRow[]>(() => {
    if (!data?.length) return [];
    
    // Helper to get all unique months present in the data
    const getAllMonths = () => {
      const months = new Set<string>();
      data.forEach(req => {
        const dateObj = new Date(req.date);
        const month = `${getMonthAbbreviation(dateObj.getMonth())} ${dateObj.getFullYear()}`;
        months.add(month);
      });
      return Array.from(months);
    };
    
    const allMonths = getAllMonths();
    
    // Map: {employeeId}-{month} => { employeeName, employeeId, month, hoursWorked, department, residency }
    const groupMap = new Map<string, EmployeeOvertimeRow>();
    
    data.forEach(req => {
      if (req.status !== OvertimeStatus.APPROVED) return; // Only approved requests
      
      // Find employee data using primary key match
      const employee = employees.find(e => e.id === req.employee_id);
      
      // Use human-readable employee_id from employees table, fallback to UUID if not available
      const businessEmployeeId = employee?.employee_id || req.employee_id || 'Unknown';
      
      // Use employee name from database if available, fallback to request data
      const employeeName = employee?.name || req.employee_name || 'Unknown';
      
      // Use department from request (already filtered by department for dept heads)
      const department = req.department || employee?.department || 'Unknown';
      
      // Use residency from employee data with fallback and normalize display value
      const residency = getResidencyDisplayValue(employee?.residency || '');
      
      const reqDate = new Date(req.date);
      
      // For each month, check if this request falls in the correct period for this employee
      allMonths.forEach(monthStr => {
        // Parse month and year
        const [monthName, yearStr] = monthStr.split(' ');
        const year = parseInt(yearStr, 10);
        const monthIdx = new Date(`${monthName} 1, ${year}`).getMonth();
        
        let periodStart, periodEnd;
        // Use utility function to check residency
        if (isResident(residency)) {
          // 25th of previous month to 24th of current month
          periodStart = new Date(year, monthIdx - 1, 25); // previous month, 25th
          periodEnd = new Date(year, monthIdx, 24, 23, 59, 59, 999); // current month, 24th end of day
        } else {
          // Non-Resident: 1st to end of month
          periodStart = new Date(year, monthIdx, 1);
          periodEnd = new Date(year, monthIdx + 1, 0, 23, 59, 59, 999); // last day of month
        }
        
        if (reqDate >= periodStart && reqDate <= periodEnd) {
          const key = `${businessEmployeeId}-${monthStr}`;
          if (!groupMap.has(key)) {
            groupMap.set(key, {
              employeeName,
              employeeId: businessEmployeeId,
              month: monthStr,
              hoursWorked: 0,
              department,
              residency
            });
          }
          groupMap.get(key)!.hoursWorked += req.hours || 0;
        }
      });
    });
    
    // Return as array, sorted by month desc then hours desc
    return Array.from(groupMap.values()).sort((a, b) => {
      // Sort by month (desc), then by hours worked (desc)
      if (a.month !== b.month) {
        // Sort by year then month
        const dateA = new Date(a.month + ' 1');
        const dateB = new Date(b.month + ' 1');
        return dateB.getTime() - dateA.getTime();
      }
      return b.hoursWorked - a.hoursWorked;
    });
  }, [data, employees]);

  // Define columns for the data table
  const columns: DataTableColumn<EmployeeOvertimeRow>[] = [
    {
      key: 'employeeName',
      header: 'Employee Name',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, row) => {
        const getInitials = (name: string): string => {
          if (!name) return '?';
          const names = name.trim().split(' ');
          if (names.length === 1) return names[0].charAt(0).toUpperCase();
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        };

        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
                {getInitials(String(value))}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground truncate">{String(value)}</span>
              <span className="text-sm text-muted-foreground truncate">
                ID: {String(row.employeeId)}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    {
      key: 'employeeId',
      header: 'Employee ID',
      sortable: true,
      searchable: true,
      hidden: true, // Hide this since it's shown in the employee name column
      render: (value) => (
        <div className="text-center text-muted-foreground font-mono text-sm font-medium">
          {String(value)}
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'month',
      header: 'Month',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center font-medium">
          <div className="flex items-center justify-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            {String(value)}
          </div>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'hoursWorked',
      header: 'Hours Worked',
      sortable: true,
      searchable: false,
      render: (value) => {
        const hours = Number(value);
        const colorScheme = getOvertimeColorScheme(hours);
        
        return (
          <div className="text-center">
            <Badge 
              variant={colorScheme.badgeVariant} 
              className={getBadgeClasses(hours)}
            >
              <Clock className="h-3 w-3 mr-1" />
              {hours.toFixed(1)}h
            </Badge>
          </div>
        );
      },
      exportRender: (value) => Number(value).toFixed(1)
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 whitespace-normal">
            <Building2 className="h-3 w-3 mr-1" />
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'residency',
      header: 'Residency',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('positive', 'light')}`}>
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    }
  ]

  return (
    <div className="space-y-4">
      <DataTable
        data={employeeTableData}
        columns={columns}
        title="Employee Overtime Department"
        isLoading={isLoading}
        className={className}
        config={{
          searchPlaceholder: "Search by name, ID, department, or month...",
          defaultSortField: 'hoursWorked',
          defaultSortDirection: 'desc',
          rowsPerPage: 10,
          showSearch: true,
          showExport: true,
          showPagination: true,
          exportFilename: 'employee_overtime_data'
        }}
        // Use inline styles for density-based background colors
        rowStyle={(row) => {
          const bgColor = getRowColor(row.hoursWorked, isDarkMode);
          return bgColor ? { backgroundColor: bgColor } : {};
        }}
      />
      
      {/* Color Legend */}
      <div className="px-4 pb-4">
        <OvertimeColorLegend />
      </div>
    </div>
  )
}

export default EmployeeOvertimeTable 